import { useCallback, useMemo, useState } from 'react';

/**
 * useGroupedRows — raggruppa righe tabellari per `group_id` in righe-gruppo
 * espandibili, riusando una `mergeFn` esistente (es. `formatDailyTrips` /
 * `formatOrders` di `@modules/utils`) per il CONTENUTO della riga-gruppo.
 *
 * `mergeFn(rows)` deve restituire un array dove ogni elemento è:
 *  - una riga-gruppo fusa con `isGroup === true` e `group_id`/`id` = group_id;
 *  - oppure una riga singola con `isGroup` falsy (viaggio senza gruppo).
 *
 * Ritorna righe "appiattite" per il DataGrid: ogni riga-gruppo è seguita dai
 * propri figli (le righe originali) solo quando è espansa. Stato di espansione
 * interno.
 *
 * @param {Array} rows - righe originali (con eventuale `group_id`)
 * @param {(rows:Array)=>Array} mergeFn - funzione di fusione per gruppo
 * @param {object} [opts]
 * @param {string} [opts.idKey='id'] - chiave id univoca delle righe originali
 * @param {(a,b)=>number} [opts.sortFn] - comparatore opzionale sul livello-top
 */
export default function useGroupedRows(rows, mergeFn, { idKey = 'id', sortFn, childSortFn } = {}) {
  const [expanded, setExpanded] = useState(() => new Set());

  const { merged, childrenByGroup } = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const childrenByGroup = {};
    for (const r of list) {
      const gid = r?.group_id;
      if (gid) (childrenByGroup[gid] ||= []).push(r);
    }
    let merged = typeof mergeFn === 'function' ? mergeFn(list) : list;
    if (sortFn) merged = [...merged].sort(sortFn);
    return { merged, childrenByGroup };
  }, [rows, mergeFn, sortFn]);

  const toggle = useCallback((gid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid);
      else next.add(gid);
      return next;
    });
  }, []);

  const isExpanded = useCallback((gid) => expanded.has(gid), [expanded]);

  const visibleRows = useMemo(() => {
    const out = [];
    for (const m of merged) {
      const gid = m.group_id ?? m[idKey];
      const kids = m.isGroup ? childrenByGroup[gid] || [] : [];
      // Gruppo "degenere" (0/1 figli) → riga singola, niente expander.
      if (!m.isGroup || kids.length <= 1) {
        const only = kids.length === 1 ? kids[0] : m;
        out.push({ ...only, __kind: 'single', __rowId: `s:${only[idKey] ?? gid}` });
        continue;
      }
      out.push({ ...m, __kind: 'group', __rowId: `g:${gid}`, __count: kids.length });
      if (expanded.has(gid)) {
        const orderedKids = childSortFn ? [...kids].sort(childSortFn) : kids;
        for (const child of orderedKids) {
          out.push({ ...child, __kind: 'child', __rowId: `c:${child[idKey]}` });
        }
      }
    }
    return out;
  }, [merged, childrenByGroup, expanded, idKey, childSortFn]);

  const getRowId = useCallback((row) => row.__rowId, []);

  const getRowClassName = useCallback((params) => {
    const k = params.row.__kind;
    if (k === 'group') return 'tbl-row--group';
    if (k === 'child') return 'tbl-row--child';
    return '';
  }, []);

  return { visibleRows, getRowId, getRowClassName, toggle, isExpanded };
}
